import { Component, inject, effect } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslocoModule } from '@jsverse/transloco';
import { PrimaryButtonComponent } from '../../../../../shared/components/primary-button/primary-button.component';
import { TextButtonComponent } from '../../../../../shared/components/text-button/text-button.component';
import { LanguageService } from '../../../../../core/services/language.service';
import { LearningPlanDataService } from '../../services/learning-plan-data.service';

interface LoopingConfirmationDialogData {
  isEnabling: boolean;
  onConfirm: () => void;
}

@Component({
  selector: 'app-looping-confirmation-dialog',
  imports: [
    MatDialogModule,
    TranslocoModule,
    PrimaryButtonComponent,
    TextButtonComponent,
  ],
  templateUrl: './looping-confirmation-dialog.component.html',
})
export class LoopingConfirmationDialogComponent {
  private readonly dialogRef =
    inject<MatDialogRef<LoopingConfirmationDialogComponent>>(MatDialogRef);
  protected readonly data =
    inject<LoopingConfirmationDialogData>(MAT_DIALOG_DATA);
  private readonly languageService = inject(LanguageService);
  private readonly learningPlanDataService = inject(LearningPlanDataService);

  private wasLoading = false;

  constructor() {
    // Watch for loading state changes to close dialog when operation completes
    effect(() => {
      const isLoading = this.learningPlanDataService.isLoopingLoading();

      if (this.wasLoading && !isLoading) {
        this.dialogRef.close();
      }
      this.wasLoading = isLoading;
    });
  }

  get headline(): string {
    return this.translateLoopingCopy(
      'portal.trainings.detailPage.loopingDialog.enableHeadline',
      'portal.trainings.detailPage.loopingDialog.disableHeadline',
    );
  }

  get toggleExplanation(): string {
    return this.translateLoopingCopy(
      'portal.trainings.detailPage.loopingDialog.enableDescription',
      'portal.trainings.detailPage.loopingDialog.disableDescription',
    );
  }

  get toggleLogicDescription(): string {
    return this.translateLoopingCopy(
      'portal.trainings.detailPage.loopingDialog.enableToggleLogic',
      'portal.trainings.detailPage.loopingDialog.disableToggleLogic',
    );
  }

  get confirmButtonText(): string {
    return this.translateLoopingCopy(
      'portal.trainings.detailPage.loopingDialog.activateButton',
      'portal.trainings.detailPage.loopingDialog.deactivateButton',
    );
  }

  private translateLoopingCopy(enableKey: string, disableKey: string): string {
    return this.languageService.translateOnRuntime(
      this.data.isEnabling ? enableKey : disableKey,
    );
  }

  get isLoading(): boolean {
    return this.learningPlanDataService.isLoopingLoading();
  }

  get cancelButtonText(): string {
    return this.languageService.translateOnRuntime('general.cancel');
  }

  onConfirmClick(): void {
    this.data.onConfirm();
    // Don't close immediately - let the effect handler close when loading completes
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
